import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef, type ForwardedRef } from 'react'
import { colors } from '../tokens/colors.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const indeterminate1Keyframe = stylex.keyframes({
  '0%': {
    left: '-35%',
    right: '100%',
  },
  '60%': {
    left: '100%',
    right: '-90%',
  },
  '100%': {
    left: '100%',
    right: '-90%',
  },
})

const indeterminate2Keyframe = stylex.keyframes({
  '0%': {
    left: '-200%',
    right: '100%',
  },
  '60%': {
    left: '107%',
    right: '-8%',
  },
  '100%': {
    left: '107%',
    right: '-8%',
  },
})

const countdownKeyframe = stylex.keyframes({
  '0%': {
    transform: 'translateX(0%)',
  },
  '100%': {
    transform: 'translateX(-100%)',
  },
})
const dynamicStyles = stylex.create({
  determinate: (value: number) => ({
    transform: `translateX(${value - 100}%)`,
    width: '100%',
  }),
  countdown: (value: number) => ({
    animationDuration: value,
    animationFillMode: 'forwards',
    animationName: countdownKeyframe,
    animationTimingFunction: 'linear',
    width: '100%',
  }),
  color: (color: string) => ({ color }),
})

const _baseStyles = stylex.create({
  root: {
    borderRadius: 12,
    overflow: 'hidden',
    display: 'block',
    position: 'relative',
    height: 4,
  },
  background: {
    backgroundColor: colors.grey100,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  bar1: {
    borderRadius: 12,
    transition: 'transform 0.2s linear',
    backgroundColor: 'currentColor',
    position: 'absolute',
    transformOrigin: 'left center',
    bottom: 0,
    left: 0,
    top: 0,
    width: 'auto',
  },
  bar2: {
    borderRadius: 12,
    transition: 'transform 0.2s linear',
    animationDelay: '1.15s',
    animationDuration: '2.1s',
    animationIterationCount: 'infinite',
    animationName: indeterminate2Keyframe,
    animationTimingFunction: 'cubic-bezier(0.165, 0.84, 0.44, 1)',
    backgroundColor: 'currentColor',
    position: 'absolute',
    transformOrigin: 'left center',
    bottom: 0,
    left: 0,
    top: 0,
    width: 'auto',
  },
  indeterminate: {
    animationDuration: '2.1s',
    animationIterationCount: 'infinite',
    animationName: indeterminate1Keyframe,
    animationTimingFunction: 'cubic-bezier(0.65, 0.815, 0.735, 0.395)',
  },
})

export interface LinearProgressProps extends ComponentPropsWithoutRef<'span'> {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 覆蓋內部子樣式的 xStyles */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 進度值（0-100），用於 determinate 與 countdown 模式
   * @default 0
   */
  value?: number
  /**
   * 進度條變體：indeterminate（無限）、determinate（確定值）、countdown（倒數）
   * @default 'indeterminate'
   */
  variant?: 'indeterminate' | 'determinate' | 'countdown'
  /** 自訂進度條顏色 */
  color?: string
}

/**
 * LinearProgress 是線性進度條元件，支援不確定、確定值與倒數模式。
 */
export const LinearProgress = (props: LinearProgressProps) => {
  const {
    rootRef,
    rootStyle,
    value = 0,
    variant = 'indeterminate',
    color,
    xStyles,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)

  return (
    <span
      ref={rootRef}
      role="progressbar"
      {...stylex.props(
        baseStyles.root,
        !!color && dynamicStyles.color(color),
        rootStyle,
      )}
      {...rest}
    >
      <span {...stylex.props(baseStyles.background)} />
      <span {...stylex.props(
        baseStyles.bar1,
        variant === 'indeterminate' && baseStyles.indeterminate,
        variant === 'determinate' && dynamicStyles.determinate(value),
        variant === 'countdown' && dynamicStyles.countdown(value),
      )}
      />
      {variant === 'indeterminate' && <span {...stylex.props(baseStyles.bar2)} />}
    </span>
  )
}
